import React from "react";
import { motion } from "framer-motion";
import {
  FaHeadset,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaTicketAlt,
  FaCreditCard,
  FaUndoAlt,
  FaDownload,
  FaArrowRight,
} from "react-icons/fa";
import HomeFaqWideSection from "../components/HomeFaqWideSection";

const PALETTE = {
  primaryRed: "#D84E55",
  accentBlue: "#3A86FF",
  textDark: "#1A1A1A",
  textLight: "#4B5563",
  bgLight: "#F8FAFC",
  borderLight: "#E5E7EB",
  white: "#FFFFFF",
};

const SECTION_WRAP = "w-screen relative left-1/2 ml-[-50vw] overflow-hidden";
const SECTION_INNER =
  "w-full max-w-[1400px] 2xl:max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8";

const quickHelpItems = [
  {
    icon: FaTicketAlt,
    title: "Booking Help",
    desc: "Need help selecting buses, seats, boarding points, or completing your booking?",
  },
  {
    icon: FaCreditCard,
    title: "Payment Help",
    desc: "Get support for payment issues, failed transactions, or booking confirmation delays.",
  },
  {
    icon: FaDownload,
    title: "Ticket Help",
    desc: "Can’t find your e-ticket? Need to re-download or view your booking again?",
  },
  {
    icon: FaUndoAlt,
    title: "Cancellation & Refunds",
    desc: "Understand cancellation rules, refund timelines, and operator-related policy support.",
  },
];

const contactItems = [
  {
    icon: FaPhoneAlt,
    title: "Call Support",
    value: "+94 76 703 7547",
    sub: "For urgent booking-related support",
    href: "tel:+94767037547",
  },
  {
    icon: FaEnvelope,
    title: "Email Support",
    value: "support@routesbook.lk",
    sub: "Best for detailed issues and payment references",
    href: "mailto:support@routesbook.lk",
  },
  {
    icon: FaWhatsapp,
    title: "WhatsApp",
    value: "+94 76 703 7547",
    sub: "Quick help for common customer questions",
    href: "https://wa.me/94767037547",
  },
  {
    icon: FaMapMarkerAlt,
    title: "Office Address",
    value: "NDD IT Solutions, Lenaduwa Junction, Godagama, Palatuwa, Matara",
    sub: "Sri Lanka",
    href: null,
  },
];

export default function Help() {
  return (
    <div
      className="min-h-screen font-sans"
      style={{ backgroundColor: PALETTE.bgLight, color: PALETTE.textDark }}
    >
      {/* ===== Hero Section ===== */}
      <section className={SECTION_WRAP}>
        <div
          className="py-14 md:py-20"
          style={{
            background:
              "linear-gradient(180deg, rgba(216,78,85,0.08) 0%, rgba(58,134,255,0.06) 45%, rgba(255,255,255,1) 100%)",
          }}
        >
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-8 left-8 w-40 h-40 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(216,78,85,0.14)" }}
          />
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-16 right-10 w-52 h-52 rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(58,134,255,0.14)" }}
          />

          <div className={`${SECTION_INNER} relative`}>
            <div className="max-w-4xl">
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border bg-white shadow-sm"
                style={{ borderColor: PALETTE.borderLight }}
              >
                <FaHeadset style={{ color: PALETTE.primaryRed }} />
                <span className="text-sm font-semibold text-gray-700">
                  Routesbook Help Centre
                </span>
              </div>

              <h1
                className="mt-5 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight"
                style={{ color: PALETTE.textDark }}
              >
                How can we help you today?
              </h1>

              <p
                className="mt-4 text-base sm:text-lg max-w-3xl leading-8"
                style={{ color: PALETTE.textLight }}
              >
                Find answers for bus ticket booking, payments, cancellations,
                refunds, ticket downloads, and account-related questions.
                If you still need help, our support team is ready to assist you.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="tel:+94767037547"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-sm hover:shadow-md transition"
                  style={{ backgroundColor: PALETTE.primaryRed }}
                >
                  <FaPhoneAlt />
                  Contact Support
                </a>

                <a
                  href="mailto:support@routesbook.lk"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full border bg-white font-semibold hover:shadow-sm transition"
                  style={{ borderColor: PALETTE.borderLight, color: PALETTE.textDark }}
                >
                  <FaEnvelope />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Quick Help Cards ===== */}
      <section className={SECTION_WRAP}>
        <div className="py-12 md:py-14 bg-white">
          <div className={SECTION_INNER}>
            <div className="max-w-3xl">
              <h2
                className="text-2xl sm:text-3xl font-extrabold tracking-tight"
                style={{ color: PALETTE.textDark }}
              >
                Quick Help Topics
              </h2>
              <p className="mt-2 text-sm sm:text-base" style={{ color: PALETTE.textLight }}>
                Start with the most common support areas customers ask about.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
              {quickHelpItems.map((item, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -4 }}
                  className="rounded-2xl border bg-white p-5 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.20)] hover:shadow-[0_14px_34px_-18px_rgba(15,23,42,0.24)] transition"
                  style={{ borderColor: PALETTE.borderLight }}
                >
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{
                      backgroundColor: "rgba(216,78,85,0.10)",
                      color: PALETTE.primaryRed,
                    }}
                  >
                    <item.icon className="text-xl" />
                  </div>

                  <h3 className="mt-4 text-lg font-bold text-gray-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Contact Section ===== */}
      <section className={SECTION_WRAP}>
        <div
          className="py-12 md:py-16"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)",
          }}
        >
          <div className={SECTION_INNER}>
            <div className="max-w-3xl">
              <h2
                className="text-2xl sm:text-3xl font-extrabold tracking-tight"
                style={{ color: PALETTE.textDark }}
              >
                Contact Routesbook Support
              </h2>
              <p className="mt-2 text-sm sm:text-base" style={{ color: PALETTE.textLight }}>
                Reach out through the channel that works best for you.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
              {contactItems.map((item, idx) => {
                const CardContent = (
                  <motion.div
                    whileHover={{ y: -3 }}
                    className="h-full rounded-2xl border bg-white p-5 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.20)] hover:shadow-[0_14px_34px_-18px_rgba(15,23,42,0.24)] transition"
                    style={{ borderColor: PALETTE.borderLight }}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: "rgba(58,134,255,0.10)",
                          color: PALETTE.accentBlue,
                        }}
                      >
                        <item.icon className="text-lg" />
                      </div>

                      <div className="min-w-0">
                        <h3 className="text-lg font-bold text-gray-900">
                          {item.title}
                        </h3>
                        <div
                          className="mt-1 text-sm sm:text-base font-semibold break-words"
                          style={{ color: PALETTE.textDark }}
                        >
                          {item.value}
                        </div>
                        <p className="mt-2 text-sm text-gray-600">{item.sub}</p>
                      </div>
                    </div>
                  </motion.div>
                );

                if (item.href) {
                  return (
                    <a
                      key={idx}
                      href={item.href}
                      target={item.href.startsWith("http") ? "_blank" : undefined}
                      rel={item.href.startsWith("http") ? "noreferrer" : undefined}
                      className="block"
                    >
                      {CardContent}
                    </a>
                  );
                }

                return <div key={idx}>{CardContent}</div>;
              })}
            </div>

            <div
              className="mt-8 rounded-2xl border bg-white p-5 sm:p-6"
              style={{ borderColor: PALETTE.borderLight }}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    Support response guidance
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 leading-6">
                    For faster support, please include your booking reference,
                    travel date, passenger name, and payment screenshot or
                    receipt if your issue is payment-related.
                  </p>
                </div>

                <div className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <span>Need urgent help?</span>
                  <FaArrowRight style={{ color: PALETTE.primaryRed }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ Section Reused from Home ===== */}
      <HomeFaqWideSection
        palette={PALETTE}
        title="Frequently Asked Questions"
        subtitle="Quick answers for booking, ticket access, payments, cancellations, and refunds."
      />

      {/* ===== Final CTA ===== */}
      <section className={SECTION_WRAP}>
        <div className="py-12 md:py-16 bg-white">
          <div className={SECTION_INNER}>
            <div
              className="rounded-3xl border p-6 sm:p-8 md:p-10"
              style={{
                borderColor: PALETTE.borderLight,
                background:
                  "linear-gradient(135deg, rgba(216,78,85,0.06) 0%, rgba(58,134,255,0.06) 100%)",
              }}
            >
              <div className="max-w-3xl">
                <h2
                  className="text-2xl sm:text-3xl font-extrabold tracking-tight"
                  style={{ color: PALETTE.textDark }}
                >
                  Still need help?
                </h2>
                <p
                  className="mt-3 text-sm sm:text-base leading-7"
                  style={{ color: PALETTE.textLight }}
                >
                  Our support team is here to help you with booking questions,
                  payment issues, cancellations, refunds, and ticket access.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href="tel:+94767037547"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-sm hover:shadow-md transition"
                    style={{ backgroundColor: PALETTE.primaryRed }}
                  >
                    <FaPhoneAlt />
                    Call Now
                  </a>

                  <a
                    href="mailto:support@routesbook.lk"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border bg-white font-semibold hover:shadow-sm transition"
                    style={{ borderColor: PALETTE.borderLight, color: PALETTE.textDark }}
                  >
                    <FaEnvelope />
                    Send Email
                  </a>

                  <a
                    href="https://wa.me/94767037547"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border bg-white font-semibold hover:shadow-sm transition"
                    style={{ borderColor: PALETTE.borderLight, color: PALETTE.textDark }}
                  >
                    <FaWhatsapp />
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
